/**
 * Anomalies Card Component
 * Displays detected anomalies in user behavior
 */
import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Paper
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';

function AnomaliesCard({ anomaliesData, loading }) {
  if (!anomaliesData) {
    return (
      <Alert severity="info">
        No anomaly data available. Run analytics to detect anomalies.
      </Alert>
    );
  }

  const getSeverityIcon = (severity) => {
    switch (severity.toLowerCase()) {
      case 'high':
        return <ErrorIcon color="error" />;
      case 'medium':
        return <WarningIcon color="warning" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      default:
        return 'info';
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Anomaly Detection
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Identifies unusual behavior patterns such as dormant account reactivation, unusual login times,
          and access from new locations.
        </Typography>
      </Box>

      {/* Summary Stats */}
      <Box sx={{ mb: 3, display: 'flex', gap: 3 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Users Analyzed
          </Typography>
          <Typography variant="h5">
            {anomaliesData.users_analyzed}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Anomalies Detected
          </Typography>
          <Typography variant="h5" color="warning.main">
            {anomaliesData.total_anomalies}
          </Typography>
        </Box>
      </Box>

      {/* Anomalies List */}
      {anomaliesData.anomalies_detected && anomaliesData.anomalies_detected.length > 0 ? (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Detected Anomalies
          </Typography>
          <List>
            {anomaliesData.anomalies_detected.map((anomaly, index) => (
              <Paper key={index} sx={{ mb: 2 }}>
                <ListItem>
                  <ListItemIcon>
                    {getSeverityIcon(anomaly.severity)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {anomaly.display_name || anomaly.user_id}
                        </Typography>
                        <Chip
                          label={anomaly.severity.toUpperCase()}
                          color={getSeverityColor(anomaly.severity)}
                          size="small"
                        />
                        <Chip
                          label={anomaly.type.replace('_', ' ').toUpperCase()}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                          {anomaly.details}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Email: {anomaly.email || 'N/A'} | Detected: {new Date(anomaly.detected_at).toLocaleString()}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              </Paper>
            ))}
          </List>
        </>
      ) : (
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="body1">
            No anomalies detected! All user behavior patterns appear normal.
          </Typography>
        </Alert>
      )}
    </Box>
  );
}

export default AnomaliesCard;
